/**
 * SetupPage — Gefuehrte Setup-Checkliste pro Firma mit Website-Scan.
 * Zeigt, welche Integrationen fehlen und was auf der Website noch einzubauen ist.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ListChecks, Loader2, Copy, ExternalLink, CheckCircle2, XCircle,
  HelpCircle, Globe, ScanSearch, ChevronDown, ChevronUp, ArrowRight,
} from "lucide-react";
import PageHelp from "@/components/PageHelp";
import { buildTrackingSnippet, buildEmbedSnippet, buildPixelSnippet } from "@/lib/snippets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildSnippets = (companyId, pixelId) => ({
  tracking: buildTrackingSnippet(companyId),
  embed: buildEmbedSnippet(companyId),
  pixel: buildPixelSnippet(pixelId),
});

const STATUS_ICON = {
  ok: <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />,
  manual: <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" />,
  missing: <XCircle className="w-5 h-5 text-red-400 shrink-0" />,
  unknown: <HelpCircle className="w-5 h-5 text-slate-300 shrink-0" />,
};

const STATUS_BADGE = {
  ok: <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Erledigt</Badge>,
  manual: <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Manuell bestaetigt</Badge>,
  missing: <Badge className="bg-red-100 text-red-600 border-red-200 text-[10px]">Fehlt</Badge>,
  unknown: <Badge variant="outline" className="text-[10px] text-slate-400">Noch nicht geprueft</Badge>,
};

function PixelInline({ companyId, data, onChanged }) {
  const [val, setVal] = useState(data?.meta_pixel_id || "");
  useEffect(() => { setVal(data?.meta_pixel_id || ""); }, [data?.meta_pixel_id]);
  const save = async () => {
    try {
      await axios.put(`${API}/leads/settings/${companyId}`, { meta_pixel_id: val.trim() }, { withCredentials: true });
      toast.success("Pixel-ID gespeichert");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input data-testid="setup-pixel-input" className="h-8 w-56 text-xs" placeholder="z. B. 1234567890123456"
        value={val} onChange={(e) => setVal(e.target.value)} />
      <Button size="sm" className="h-8 text-xs bg-[#2563EB] hover:bg-[#1d4ed8]" onClick={save} data-testid="setup-pixel-save">
        Pixel-ID speichern
      </Button>
    </div>
  );
}

function PixelConfirmInline({ companyId, data, item, onChanged }) {
  const toggle = async (confirmed) => {
    try {
      await axios.put(`${API}/setup/${companyId}/pixel-confirm`, { confirmed }, { withCredentials: true });
      toast.success(confirmed ? "Einbau manuell bestaetigt" : "Bestaetigung zurueckgenommen");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  return (
    <div className="space-y-2">
      {item.status_note && (
        <p className={`text-[11px] font-medium ${item.status === "ok" ? "text-emerald-600" : "text-amber-600"}`} data-testid="setup-pixel-status-note">
          {item.status_note}
        </p>
      )}
      {item.status !== "ok" && (
        data?.pixel_confirmed ? (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toggle(false)} data-testid="setup-pixel-unconfirm">
            Bestaetigung zuruecknehmen
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toggle(true)} data-testid="setup-pixel-confirm">
            Einbau manuell bestaetigen
          </Button>
        )
      )}
    </div>
  );
}

function AdsAccountInline({ companyId, data, onChanged, navigate }) {
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const ms = data?.meta_status || {};

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ads/accounts`, { params: { company_id: companyId }, withCredentials: true });
      setAccounts(r.data.accounts || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Werbekonten konnten nicht geladen werden"); }
    setLoading(false);
  };

  const select = async (a) => {
    try {
      await axios.post(`${API}/ads/accounts/select`, { company_id: companyId, ad_account_id: a.id, ad_account_name: a.name }, { withCredentials: true });
      toast.success(`Werbekonto '${a.name}' zugewiesen`);
      setAccounts(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen"); }
  };

  const unselect = async () => {
    if (!window.confirm("Werbekonto-Zuweisung von dieser Firma entfernen? Das Werbekonto selbst bleibt unveraendert.")) return;
    try {
      await axios.post(`${API}/ads/accounts/unselect`, { company_id: companyId }, { withCredentials: true });
      toast.success("Werbekonto-Zuweisung entfernt");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  if (!ms.social_connected) {
    return (
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
        Zuerst Meta (Social) verbinden — danach kann hier das Werbekonto ausgewaehlt werden.
        <Button size="sm" variant="outline" className="h-6 text-[11px] ml-2" onClick={() => navigate("/social-media")}>Zu Social Media</Button>
      </div>
    );
  }
  if (!ms.ads_scope) {
    return (
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
        Die Ads-Berechtigung fehlt im aktuellen Meta-Login. Bitte Meta neu verbinden und im Facebook-Dialog die Werbekonto-Berechtigung erteilen.
        <Button size="sm" variant="outline" className="h-6 text-[11px] ml-2" onClick={() => navigate("/social-media")}>Meta neu verbinden</Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {ms.ad_account_id ? (
        <div className="flex items-center gap-2 text-xs">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          <span>Verbundenes Werbekonto: <b>{ms.ad_account_name || ms.ad_account_id}</b></span>
          <button onClick={unselect} className="text-red-500 hover:underline text-[11px]" data-testid="setup-ads-unselect">Entfernen</button>
        </div>
      ) : accounts === null ? (
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={loadAccounts} disabled={loading} data-testid="setup-ads-load">
          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Verfuegbare Werbekonten anzeigen
        </Button>
      ) : accounts.length === 0 ? (
        <p className="text-xs text-amber-600">Keine Werbekonten gefunden — pruefe im Meta Business Manager, ob dein Konto Zugriff auf ein Werbekonto hat.</p>
      ) : (
        <div className="space-y-1">
          {accounts.map((a) => (
            <div key={a.id} className="flex items-center justify-between text-xs p-2 border border-[var(--studio-border)] rounded">
              <span>{a.name} <span className="text-slate-400">({a.id} · {a.currency})</span></span>
              <Button size="sm" className="h-6 text-[11px] bg-[#2563EB] hover:bg-[#1d4ed8]" onClick={() => select(a)} data-testid={`setup-ads-select-${a.id}`}>
                Auswaehlen
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChecklistItem({ item, snippets, navigate, companyId, data, onChanged }) {
  const [open, setOpen] = useState(false);
  const snippet = item.snippet_type ? snippets[item.snippet_type] : null;

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet);
    toast.success("Code kopiert");
  };

  return (
    <div className="border border-[var(--studio-border)] rounded-lg bg-white" data-testid={`setup-item-${item.key}`}>
      <button
        className="w-full flex items-center gap-3 p-3 text-left"
        onClick={() => setOpen((o) => !o)}
        data-testid={`setup-item-toggle-${item.key}`}
      >
        {STATUS_ICON[item.status]}
        <span className="flex-1 text-sm font-medium">{item.title}</span>
        {STATUS_BADGE[item.status]}
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-[var(--studio-border)]">
          <p className="text-xs text-slate-600 pt-2">{item.description}</p>
          {item.inline === "pixel" && <PixelInline companyId={companyId} data={data} onChanged={onChanged} />}
          {item.inline === "pixel_confirm" && <PixelConfirmInline companyId={companyId} data={data} item={item} onChanged={onChanged} />}
          {item.inline === "ads_account" && <AdsAccountInline companyId={companyId} data={data} onChanged={onChanged} navigate={navigate} />}
          <div className="flex flex-wrap gap-2">
            {item.action_path && (
              <Button
                size="sm"
                className="h-7 text-xs bg-[#2563EB] hover:bg-[#1d4ed8]"
                onClick={() => navigate(item.action_path)}
                data-testid={`setup-action-${item.key}`}
              >
                {item.action_label} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
            {(item.links || []).map((l) => (
              <Button
                key={l.url}
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => window.open(l.url, "_blank", "noopener")}
                data-testid={`setup-link-${item.key}-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <ExternalLink className="w-3 h-3 mr-1" /> {l.label}
              </Button>
            ))}
          </div>
          {snippet && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-slate-500">Copy-Paste Code</span>
                <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={copySnippet} data-testid={`setup-copy-${item.key}`}>
                  <Copy className="w-3 h-3 mr-1" /> Kopieren
                </Button>
              </div>
              <pre className="text-[10px] bg-slate-50 border border-[var(--studio-border)] rounded-md p-2.5 overflow-x-auto whitespace-pre-wrap break-all max-h-52">{snippet}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlanSettings({ companyId, plan, onChanged }) {
  const [form, setForm] = useState({ posts_per_week: 3, reels_per_week: 1, stories_per_week: 0 });
  useEffect(() => {
    setForm({
      posts_per_week: plan?.posts_per_week ?? 3,
      reels_per_week: plan?.reels_per_week ?? 1,
      stories_per_week: plan?.stories_per_week ?? 0,
    });
  }, [plan, companyId]);
  const save = async () => {
    try {
      await axios.put(`${API}/companies/${companyId}`, form, { withCredentials: true });
      toast.success("Wochenplanung gespeichert");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  const F = (key, label) => (
    <div>
      <label className="text-[10px] uppercase text-slate-500">{label}</label>
      <Input type="number" min="0" max="50" className="mt-1 h-8"
        value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: parseInt(e.target.value || "0") }))}
        data-testid={`setup-plan-${key}`} />
    </div>
  );
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="grid grid-cols-3 gap-3 flex-1 min-w-[280px]">
        {F("posts_per_week", "Posts / Woche")}
        {F("reels_per_week", "Reels / Woche")}
        {F("stories_per_week", "Stories / Woche")}
      </div>
      <Button size="sm" className="h-8 text-xs bg-[#2563EB] hover:bg-[#1d4ed8]" onClick={save} data-testid="setup-plan-save">Speichern</Button>
    </div>
  );
}

export default function SetupPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [data, setData] = useState(null);
  const [domain, setDomain] = useState("");
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => { setCompanies(r.data || []); if (r.data?.length) setCompanyId(r.data[0].id); })
      .catch(() => {});
  }, []);

  const load = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const r = await axios.get(`${API}/setup/${cid}/checklist`, { withCredentials: true });
      setData(r.data);
      setDomain(r.data.website_domain || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    }
  }, []);

  useEffect(() => { load(companyId); }, [companyId, load]);

  const scan = async () => {
    if (!domain.trim()) { toast.error("Bitte Domain eintragen (z.B. www.firma.de)"); return; }
    setScanning(true);
    try {
      const r = await axios.post(`${API}/setup/${companyId}/scan`, { domain: domain.trim() }, { withCredentials: true });
      setData(r.data);
      if (r.data.scan?.error) toast.error(`Website nicht erreichbar: ${r.data.scan.error}`);
      else toast.success("Website gescannt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan fehlgeschlagen");
    } finally { setScanning(false); }
  };

  const snippets = companyId ? buildSnippets(companyId, data?.meta_pixel_id) : {};
  const integrationItems = (data?.items || []).filter((i) => i.area === "integration");
  const websiteItems = (data?.items || []).filter((i) => i.area === "website");
  const pct = data ? Math.round((data.progress.done / data.progress.total) * 100) : 0;

  return (
    <div className="p-6 space-y-5" data-testid="setup-page">
      <PageHelp
        title="Setup-Anleitung"
        text="Gefuehrte Checkliste pro Firma: Welche Integrationen sind verbunden und was fehlt noch auf der Firmen-Website? Domain eintragen und scannen — die App prueft automatisch, ob Meta Pixel, Lead-Formular und Tracking-Snippet eingebaut sind. Jeder offene Punkt enthaelt Copy-Paste-Code und Direktlinks zu den noetigen Portalen."
      />
      <div className="flex flex-wrap items-center gap-3">
        <ListChecks className="w-6 h-6 text-[#2563EB]" />
        <h1 className="text-2xl font-bold" data-testid="setup-title">
          Setup-Anleitung{data?.company_name ? ` — ${data.company_name}` : ""}
        </h1>
        <div className="flex-1" />
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-56" data-testid="setup-company-select"><SelectValue placeholder="Firma" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {data && (
        <>
          <Card>
            <CardContent className="pt-5 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <Globe className="w-4 h-4 text-[#2563EB] shrink-0" />
                <Input
                  data-testid="setup-domain-input"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="www.firmen-website.de"
                  className="flex-1 min-w-[220px]"
                />
                <Button onClick={scan} disabled={scanning} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="setup-scan-btn">
                  {scanning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ScanSearch className="w-4 h-4 mr-1.5" />}
                  Website scannen
                </Button>
              </div>
              {data.scan?.scanned_at && (
                <p className="text-[11px] text-slate-500" data-testid="setup-scan-info">
                  Letzter Scan: {new Date(data.scan.scanned_at).toLocaleString("de-DE")}
                  {data.scan.url ? ` — ${data.scan.url}` : ""}
                  {data.scan.error ? ` — Fehler: ${data.scan.error}` : ""}
                </p>
              )}
              <div className="flex items-center gap-3">
                <Progress value={pct} className="h-2 flex-1" />
                <span className="text-xs font-semibold text-slate-600" data-testid="setup-progress-text">
                  {data.progress.done}/{data.progress.total} erledigt
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Integrationen in InvictaFlow</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {integrationItems.map((item) => (
                <ChecklistItem key={item.key} item={item} snippets={snippets} navigate={navigate} companyId={companyId} data={data} onChanged={() => load(companyId)} />
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Wochenplanung</CardTitle>
              <p className="text-xs text-slate-500">Wie viele Beitraege soll der Wochenplaner fuer diese Firma pro Woche vorschlagen?</p>
            </CardHeader>
            <CardContent>
              <PlanSettings companyId={companyId} plan={data.plan} onChanged={() => load(companyId)} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Auf der Website einbauen</CardTitle>
              <p className="text-xs text-slate-500">Diese Punkte werden per Website-Scan automatisch geprueft.</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {websiteItems.map((item) => (
                <ChecklistItem key={item.key} item={item} snippets={snippets} navigate={navigate} companyId={companyId} data={data} onChanged={() => load(companyId)} />
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
